import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { BRAND, CONTACT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", message: "", company_website_url: "" };
const EASE = [0.16, 1, 0.3, 1];

const inputClass =
  "w-full border border-amber-500/15 bg-[#121110] px-5 py-4 text-sm text-[#FFF5EB] placeholder:text-neutral-600 outline-none transition-all duration-300 focus:border-ember/60 focus:shadow-[0_0_30px_rgba(230,81,0,0.12)]";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry received — we'll be in touch within 72 hours.");
      setForm(EMPTY);
    } catch {
      toast.error(`Something went wrong — please email us directly at ${BRAND.email}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative overflow-hidden border-t border-amber-500/10 bg-coal px-6 py-24 sm:px-10 lg:px-16 lg:py-32"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 top-0 h-[500px] w-[500px] rounded-full bg-ember/15 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 bottom-0 h-[500px] w-[500px] rounded-full bg-crimson/15 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/10 blur-[160px]"
      />

      <div className="relative grid gap-14 lg:grid-cols-2 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-gold">
            Inquiries
          </p>
          <h2 className="bg-gradient-to-r from-amber-100 via-gold to-ember bg-clip-text font-serif text-2xl uppercase tracking-tight text-transparent sm:text-3xl lg:text-4xl">
            {CONTACT.heading}
          </h2>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-neutral-300 sm:text-lg">
            {CONTACT.lead}
          </p>
          <div className="group mt-10 border border-amber-500/15 bg-[#141210] p-6 transition-all duration-500 hover:border-ember/50 hover:shadow-[0_0_40px_rgba(230,81,0,0.15)]">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              Prefer email?
            </p>
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="contact-direct-email"
              className="mt-3 inline-flex items-center gap-3 font-serif text-xl text-amber-200 transition-colors hover:text-amber-400"
            >
              <Mail className="size-5 text-ember" strokeWidth={1.5} />
              {BRAND.email}
            </a>
          </div>
          <p className="mt-8 max-w-md font-mono text-sm uppercase leading-relaxed tracking-[0.2em] text-ember/90">
            Now booking private events, estate gatherings &amp; wedding parties — season by
            season, a limited number of fires each month.
          </p>
        </motion.div>

        <motion.form
          data-testid="contact-inquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="relative border border-amber-500/20 bg-hearth p-8 shadow-[0_0_60px_rgba(230,81,0,0.08)] md:p-10"
        >
          <div className="space-y-6">
            <div>
              <label htmlFor="inquiry-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                Full Name
              </label>
              <input
                id="inquiry-name"
                data-testid="contact-input-name"
                required
                maxLength={120}
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="inquiry-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                Email Address
              </label>
              <input
                id="inquiry-email"
                data-testid="contact-input-email"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="inquiry-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                Your Event
              </label>
              <textarea
                id="inquiry-message"
                data-testid="contact-input-message"
                required
                maxLength={5000}
                rows={6}
                value={form.message}
                onChange={set("message")}
                placeholder="Date, location, guest count, the mood you're dreaming of…"
                className={`${inputClass} resize-y`}
              />
            </div>

            {/* Honeypot — invisible to humans, bots fill it and get silently discarded */}
            <input
              data-testid="contact-input-honeypot"
              type="text"
              name="company_website_url"
              value={form.company_website_url}
              onChange={set("company_website_url")}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="pointer-events-none absolute -z-10 opacity-0"
            />

            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group inline-flex w-full items-center justify-center gap-3 bg-ember px-7 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#0A0908] transition-all duration-300 hover:bg-amber-400 hover:shadow-[0_0_50px_rgba(230,81,0,0.45)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send Inquiry"}
              <Send className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-ember/90">
              No obligation — just a conversation about your table
            </p>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
